import { IconArrowRight, IconBooks, IconCalendarEvent, IconClockHour4, IconMapPin, IconPhone, IconSpeakerphone, IconUser } from '@tabler/icons-react';
import { useTranslation } from '../../../../i18n/hooks/useTranslation';
import { PosterLogo, PosterSheet, fs, hexToRgba, numberScale, posterFields, valueFitScale } from '../posterParts';

/**
 * Landscape "Billboard" — a dark, full-bleed poster like a web hero: glowing colour behind a diagonal band, the organisation as a huge
 * headline, qualification / age / start as glass chips, the vacancy count in a big gradient disc, and the call-to-action next to the shop.
 * Palette: bg1, bg2, glowA, glowB, accent, accent2, ink (text on the disc / accent).
 */
const FONT = "'Anek Bangla', 'Noto Sans Bengali', sans-serif";
const GLASS = { background: 'rgba(255,255,255,0.08)', border: '0.2cqw solid rgba(255,255,255,0.2)' };

function Chip({ p, Icon, label, children }) {
    return (
        <div className="flex items-center" style={{ ...GLASS, borderRadius: fs(1.8), padding: `${fs(1.1)} ${fs(1.8)}`, gap: fs(1.4), maxWidth: '100%' }}>
            <div className="flex flex-none items-center justify-center rounded-full" style={{ width: fs(4.6), height: fs(4.6), background: hexToRgba(p.accent, 0.2), border: `0.2cqw solid ${hexToRgba(p.accent, 0.7)}` }}>
                <Icon style={{ width: fs(2.6), height: fs(2.6), color: p.accent }} stroke={2.2} />
            </div>
            <div className="min-w-0">
                <div style={{ color: hexToRgba('#ffffff', 0.7), fontSize: fs(1.55), fontWeight: 700, lineHeight: 1.15 }}>{label}</div>
                {children}
            </div>
        </div>
    );
}

export default function LandscapeBillboardDesign({ job, business, theme, showFooter = true, className = '' }) {
    const { t } = useTranslation();
    const p = theme.palette;
    const f = posterFields(job, business, t);
    const eduScale = valueFitScale(f.edu, 60);
    const ageScale = valueFitScale(f.age, 40);

    const decor = (
        <>
            <div className="pointer-events-none absolute" style={{ right: '-6cqw', top: '-30cqw', width: '46cqw', height: '140cqw', transform: 'rotate(17deg)', background: `linear-gradient(180deg, ${hexToRgba(p.accent, 0.2)}, ${hexToRgba(p.accent2, 0.05)})` }} />
            <div className="pointer-events-none absolute" style={{ right: '-1cqw', top: '-30cqw', width: '3cqw', height: '140cqw', transform: 'rotate(17deg)', background: `linear-gradient(180deg, ${hexToRgba(p.accent, 0.55)}, transparent)` }} />
        </>
    );

    const body = (
        <div className="flex flex-1 flex-col" style={{ padding: `${fs(3.6)} 4.6cqw ${fs(2.8)}`, gap: fs(2.2) }}>
            <div className="flex items-center" style={{ gap: fs(2) }}>
                <PosterLogo job={job} size={9.5} style={{ background: '#fff', borderRadius: fs(2.2), padding: fs(0.9), boxShadow: `0 0 2cqw ${hexToRgba(p.accent, 0.4)}` }} />
                <div className="flex items-center" style={{ background: p.accent, color: p.ink, borderRadius: '99px', padding: `${fs(0.7)} ${fs(2)}`, gap: fs(0.9), fontSize: fs(2), fontWeight: 900 }}>
                    <IconSpeakerphone style={{ width: fs(2.4), height: fs(2.4) }} stroke={2.4} />
                    <div>{f.label.notice}</div>
                </div>
                <div className="flex-1" />
                <div className="flex items-center" style={{ ...GLASS, borderRadius: '99px', padding: `${fs(0.7)} ${fs(2)}`, gap: fs(1), color: '#fff', fontSize: fs(2), fontWeight: 800, boxShadow: `0 0 1.6cqw ${hexToRgba(p.accent2, 0.5)}` }}>
                    <IconClockHour4 style={{ width: fs(2.4), height: fs(2.4), color: p.accent2 }} stroke={2.2} />
                    <div>{f.label.end}: {f.end}</div>
                </div>
            </div>

            <div className="flex flex-1 items-center" style={{ gap: fs(3.4) }}>
                <div className="flex min-w-0 flex-1 flex-col" style={{ gap: fs(1.8) }}>
                    <div>
                        <div style={{ color: '#fff', fontSize: fs(5.6), fontWeight: 900, lineHeight: 1.1, textShadow: `0 0 2.4cqw ${hexToRgba(p.glowA, 0.6)}` }}>{f.org}</div>
                        {f.position && <div style={{ color: p.accent, fontSize: fs(2.5), fontWeight: 700, lineHeight: 1.3, marginTop: fs(0.9) }}>{f.position}</div>}
                    </div>
                    <div className="flex flex-wrap" style={{ gap: fs(1.3) }}>
                        <Chip p={p} Icon={IconBooks} label={f.label.edu}>
                            <div style={{ color: '#fff', fontSize: `calc(${2.2 * eduScale}cqw * var(--fs, 1))`, fontWeight: 800, lineHeight: 1.2 }}>{f.edu}</div>
                        </Chip>
                        <Chip p={p} Icon={IconUser} label={f.label.age}>
                            <div style={{ color: '#fff', fontSize: `calc(${2.2 * ageScale}cqw * var(--fs, 1))`, fontWeight: 800, lineHeight: 1.2 }}>{f.age}</div>
                        </Chip>
                        <Chip p={p} Icon={IconCalendarEvent} label={f.label.start}>
                            <div style={{ color: '#fff', fontSize: fs(2.2), fontWeight: 800, lineHeight: 1.2 }}>{f.start}</div>
                        </Chip>
                    </div>
                </div>

                <div className="relative flex flex-none flex-col items-center justify-center rounded-full text-center" style={{ width: fs(29), height: fs(29), background: `linear-gradient(145deg, ${p.accent}, ${p.accent2})`, color: p.ink, boxShadow: `0 0 0 ${fs(1)} ${hexToRgba(p.accent, 0.25)}, 0 0 0 ${fs(2)} ${hexToRgba(p.accent, 0.12)}, 0 0 5cqw ${hexToRgba(p.accent2, 0.55)}` }}>
                    <div style={{ fontSize: fs(2.1), fontWeight: 800, lineHeight: 1 }}>{f.label.vacancy}</div>
                    <div style={{ fontSize: `calc(${11.5 * numberScale(f.vacancy)}cqw * var(--fs, 1))`, fontWeight: 900, lineHeight: 1.02 }}>{f.vacancy}</div>
                    <div style={{ fontSize: fs(2.7), fontWeight: 800, lineHeight: 1 }}>{f.label.unit}</div>
                </div>
            </div>

            <div className="flex items-stretch" style={{ gap: fs(2) }}>
                <div className="flex items-center justify-between" style={{ flex: '0 0 44%', background: `linear-gradient(90deg, ${p.accent}, ${p.accent2})`, color: p.ink, borderRadius: '99px', padding: `${fs(1.4)} ${fs(2.6)}`, boxShadow: `0 0 3cqw ${hexToRgba(p.accent2, 0.45)}` }}>
                    <div style={{ fontSize: fs(2.8), fontWeight: 900, lineHeight: 1.15 }}>{f.label.cta}</div>
                    <IconArrowRight className="flex-none" style={{ width: fs(3.6), height: fs(3.6) }} stroke={3} />
                </div>
                {showFooter && (
                    <div className="flex min-w-0 flex-1 items-center" style={{ ...GLASS, borderRadius: fs(2.4), padding: `${fs(1.2)} ${fs(2.2)}`, gap: fs(1.8) }}>
                        {f.shop.logo && <img src={f.shop.logo} alt="" className="flex-none rounded-full object-contain" style={{ width: fs(5.8), height: fs(5.8), background: '#fff', padding: '0.4cqw' }} />}
                        <div className="min-w-0 flex-1">
                            <div style={{ color: p.accent, fontSize: fs(2.6), fontWeight: 900, lineHeight: 1.12, opacity: f.shop.placeholder ? 0.65 : 1 }}>{f.shop.nameShown}</div>
                            <div className="flex items-center" style={{ gap: fs(0.7), color: '#fff', fontSize: fs(1.85), lineHeight: 1.25, opacity: f.shop.addressPlaceholder ? 0.6 : 0.9, marginTop: fs(0.3) }}>
                                <IconMapPin className="flex-none" style={{ width: fs(2.1), height: fs(2.1), color: p.accent }} />
                                <div>{f.shop.addressShown}</div>
                            </div>
                        </div>
                        {f.shop.phone && (
                            <div className="flex flex-none items-center" style={{ gap: fs(0.7), color: '#fff', fontSize: fs(2.5), fontWeight: 900 }}>
                                <IconPhone className="flex-none" style={{ width: fs(2.6), height: fs(2.6), color: p.accent }} />
                                <div>{f.shop.phone}</div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    const background = [
        `radial-gradient(60% 95% at 10% -5%, ${hexToRgba(p.glowA, 0.55)}, transparent 72%)`,
        `radial-gradient(52% 85% at 105% 105%, ${hexToRgba(p.glowB, 0.45)}, transparent 72%)`,
        `linear-gradient(135deg, ${p.bg1} 0%, ${p.bg2} 100%)`,
    ].join(', ');

    return <PosterSheet landscape deps={[job, business, theme, showFooter, t]} className={className} fontFamily={FONT} background={background} decor={decor} body={body} />;
}
